import { useSearch } from "@tanstack/react-router"
import { useCallback } from "react"
import type { Client, CombinedError } from "urql"

import type { CompetitorProduct, CompetitorProductSortField, SortingOrder } from "@/graphql/codegen/graphql"
import { queryClient } from "@/providers/GraphqlRouterProvider"
import type { CompetitorProductSearchFilters } from "@/screens/ManageProducts/stores/useCompetitorProductSearchStore"
import { useCompetitorProductSearchStore } from "@/screens/ManageProducts/stores/useCompetitorProductSearchStore"

interface UseCompetitorProductSearchListReturn {
  searchParams: {
    query?: string
    field?: CompetitorProductSortField
    order?: SortingOrder
    distributorIds?: string[]
  }
  data: CompetitorProduct[]
  fetching: boolean
  error: CombinedError | Error | undefined
  executeSearch: (client: Client, filters: CompetitorProductSearchFilters, skipFilterCheck?: boolean) => Promise<void>
  refetchData: (skipCheck?: boolean) => Promise<void>
  clearStore: () => void
}

const useCompetitorProductSearchList = (): UseCompetitorProductSearchListReturn => {
  const currentSearch = useSearch({ from: "/app/admin/manage-products/_layout/competitor-products" })
  const { query, field, order, distributorIds } = currentSearch

  const executeSearch = useCompetitorProductSearchStore(({ executeSearch }) => executeSearch)
  const data = useCompetitorProductSearchStore(({ data }) => data)
  const fetching = useCompetitorProductSearchStore(({ fetching }) => fetching)
  const error = useCompetitorProductSearchStore(({ error }) => error)
  const clearStore = useCompetitorProductSearchStore(({ clearStore }) => clearStore)

  const refetchData = useCallback(
    async (skipCheck = true) => {
      return executeSearch(queryClient, currentSearch, skipCheck)
    },
    [currentSearch, executeSearch]
  )

  return {
    searchParams: {
      query,
      field,
      order,
      distributorIds,
    },
    data,
    fetching,
    error,
    executeSearch,
    refetchData,
    clearStore,
  }
}

export default useCompetitorProductSearchList
